import React, { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import { creditService } from '../services/creditService'

const usageTypeLabel = (usageType) => {
  switch (usageType) {
    case 'WORKFLOW':
      return 'Lead Discovery'
    case 'ORGANISATION_SUMMARY':
      return 'Org Summary'
    case 'LEAD_SUMMARY':
      return 'Lead Summary'
    case 'CAMPAIGN_EMAIL':
      return 'Campaign Email'
    case 'CAMPAIGN':
      return 'Campaign'
    default:
      return null
  }
}

const usageTypeBadgeClass = (usageType) => {
  switch (usageType) {
    case 'WORKFLOW':
      return 'bg-blue-50 text-blue-700'
    case 'ORGANISATION_SUMMARY':
      return 'bg-amber-50 text-amber-700'
    case 'LEAD_SUMMARY':
      return 'bg-teal-50 text-teal-700'
    case 'CAMPAIGN_EMAIL':
      return 'bg-pink-50 text-pink-700'
    case 'CAMPAIGN':
      return 'bg-indigo-50 text-indigo-700'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

const CreditBalance = () => {
  const [creditBalances, setCreditBalances] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchParams, setSearchParams] = useState({
    organisation_name: '',
    website: ''
  })
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [showTransactionsModal, setShowTransactionsModal] = useState(false)
  const [selectedOrganisation, setSelectedOrganisation] = useState(null)
  const [organisations, setOrganisations] = useState([])
  const [orgTransactions, setOrgTransactions] = useState([])
  const [transactionsLoading, setTransactionsLoading] = useState(false)

  const [createForm, setCreateForm] = useState({
    organisation_id: '',
    credit_balance: 0.00
  })
  const [creditForm, setCreditForm] = useState({
    credit_amount: '',
    dollar_amount: ''
  })

  useEffect(() => {
    fetchCreditBalances()
    fetchOrganisations()
  }, [])

  const fetchCreditBalances = async () => {
    try {
      setLoading(true)
      // Filter out empty parameters
      const filteredParams = Object.fromEntries(
        Object.entries(searchParams).filter(([_, value]) => value && value.trim() !== '')
      )
      const response = await creditService.getCreditBalances(filteredParams)
      setCreditBalances(response.data || [])
    } catch (error) {
      console.error('Error fetching credit balances:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchOrganisations = async () => {
    try {
      const response = await creditService.getOrganisations()
      setOrganisations(response.data || [])
    } catch (error) {
      console.error('Error fetching organisations:', error)
    }
  }

  const handleSearch = () => {
    fetchCreditBalances()
  }

  const handleCreateCreditBalance = async (e) => {
    e.preventDefault()
    try {
      await creditService.createCreditBalance(createForm)
      setShowCreateModal(false)
      setCreateForm({ organisation_id: '', credit_balance: 0.00 })
      fetchCreditBalances()
    } catch (error) {
      // Error handling is done in the service
    }
  }

  const handleAddCredit = async (e) => {
    e.preventDefault()
    try {
      await creditService.addCreditTransaction(selectedOrganisation.organisation_id, {
        organisation_id: selectedOrganisation.organisation_id,
        credit_amount: parseFloat(creditForm.credit_amount),
        dollar_amount: parseFloat(creditForm.dollar_amount)
      })
      setShowCreditModal(false)
      setCreditForm({ credit_amount: '', dollar_amount: '' })
      setSelectedOrganisation(null)
      fetchCreditBalances()
    } catch (error) {
      // Error handling is done in the service
    }
  }

  const openCreditModal = (organisation) => {
    setSelectedOrganisation(organisation)
    setShowCreditModal(true)
  }

  const openTransactionsModal = async (organisation) => {
    setSelectedOrganisation(organisation)
    setShowTransactionsModal(true)
    setTransactionsLoading(true)
    try {
      const response = await creditService.getCreditTransactions(organisation.organisation_id)
      setOrgTransactions(response.data || [])
    } catch (error) {
      console.error('Error fetching transactions:', error)
      setOrgTransactions([])
    } finally {
      setTransactionsLoading(false)
    }
  }

  const formatCredits = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  const formatWhen = (iso) => {
    if (!iso) return '—'
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    } catch {
      return '—'
    }
  }

  return (
    <div>
      <div className="card mb-6">
        <div className="flex flex-col lg:flex-row gap-4 mb-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search by Organisation Name
            </label>
            <input
              type="text"
              value={searchParams.organisation_name}
              onChange={(e) => setSearchParams({ ...searchParams, organisation_name: e.target.value })}
              className="input-field"
              placeholder="Enter organisation name..."
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search by Website
            </label>
            <input
              type="text"
              value={searchParams.website}
              onChange={(e) => setSearchParams({ ...searchParams, website: e.target.value })}
              className="input-field"
              placeholder="Enter website..."
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleSearch}
              className="btn-primary"
            >
              Search
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Create Balance
            </button>
          </div>
        </div>
      </div>

      {/* Credit Balances Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Credit Balances</h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading credit balances...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organisation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Website
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credit Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {creditBalances.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      No credit balances found
                    </td>
                  </tr>
                ) : (
                  creditBalances.map((balance) => (
                    <tr key={balance.organisation_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {balance.organisation_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {balance.website || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-green-600">
                          {balance.credit_balance.toFixed(2)}C
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(balance.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => openCreditModal(balance)}
                          className="text-purple-600 hover:text-purple-900 mr-3"
                        >
                          Add Credit
                        </button>
                        <button
                          onClick={() => openTransactionsModal(balance)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Transactions
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Credit Balance Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create Credit Balance</h3>
              <form onSubmit={handleCreateCreditBalance}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organisation
                  </label>
                  <select
                    value={createForm.organisation_id}
                    onChange={(e) => setCreateForm({ ...createForm, organisation_id: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Select Organisation</option>
                    {organisations.map((org) => (
                      <option key={org.organisation_id} value={org.organisation_id}>
                        {org.organisation_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Initial Credit Balance
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={createForm.credit_balance}
                    onChange={(e) => setCreateForm({ ...createForm, credit_balance: parseFloat(e.target.value) || 0 })}
                    className="input-field"
                    required
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Transactions Modal */}
      {showTransactionsModal && selectedOrganisation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Transactions — {selectedOrganisation.organisation_name}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Balance: {parseFloat(selectedOrganisation.credit_balance || 0).toFixed(2)}C
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowTransactionsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold px-2"
              >
                ×
              </button>
            </div>

            {transactionsLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
                <p className="mt-2 text-gray-600">Loading transactions…</p>
              </div>
            ) : orgTransactions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No transactions found for this organisation.
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Usage
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orgTransactions.map((tx) => (
                      <tr key={tx.transaction_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                              tx.transaction_type === 'D'
                                ? 'bg-red-50 text-red-700'
                                : tx.transaction_type === 'C'
                                  ? 'bg-green-50 text-green-700'
                                  : 'bg-blue-50 text-blue-700'
                            }`}
                          >
                            {tx.transaction_type === 'D'
                              ? 'Debit'
                              : tx.transaction_type === 'C'
                                ? 'Credit'
                                : 'Refund'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {usageTypeLabel(tx.usage_type) ? (
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${usageTypeBadgeClass(
                                tx.usage_type
                              )}`}
                            >
                              {usageTypeLabel(tx.usage_type)}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="space-y-0.5">
                            {tx.model_name && (
                              <p className="text-xs">
                                <span className="font-medium text-gray-500">Model:</span>{' '}
                                {tx.model_name}
                              </p>
                            )}
                            {tx.campaign_name && (
                              <p className="text-xs">
                                <span className="font-medium text-gray-500">Campaign:</span>{' '}
                                {tx.campaign_name}
                              </p>
                            )}
                            {tx.workflow_id && (
                              <p className="text-xs">
                                <span className="font-medium text-gray-500">Workflow:</span>{' '}
                                <span className="font-mono">{String(tx.workflow_id).slice(0, 8)}…</span>
                              </p>
                            )}
                            {tx.dollar_amount != null && tx.dollar_amount !== '' && (
                              <p className="text-xs text-gray-400">
                                ${parseFloat(tx.dollar_amount).toFixed(4)}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold">
                          <span
                            className={
                              tx.transaction_type === 'D'
                                ? 'text-red-600'
                                : 'text-green-600'
                            }
                          >
                            {tx.transaction_type === 'D' ? '-' : '+'}
                            {formatCredits(tx.credit_amount)}C
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {formatWhen(tx.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowTransactionsModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Credit Modal */}
      {showCreditModal && selectedOrganisation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Add Credit - {selectedOrganisation.organisation_name}
              </h3>
              <form onSubmit={handleAddCredit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Credit Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={creditForm.credit_amount}
                    onChange={(e) => setCreditForm({ ...creditForm, credit_amount: e.target.value })}
                    className="input-field"
                    placeholder="Enter credit amount"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dollar Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={creditForm.dollar_amount}
                    onChange={(e) => setCreditForm({ ...creditForm, dollar_amount: e.target.value })}
                    className="input-field"
                    placeholder="Enter dollar amount"
                    required
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreditModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Add Credit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreditBalance
